import { type RefObject, useCallback, useEffect, useState } from "react";

const EDGE_PX = 1;
const PAGE_RATIO = 0.7;

export type OverflowCueState = {
  overflows: boolean;
  canPrev: boolean;
  canNext: boolean;
  scrollByPage: (direction: -1 | 1) => void;
};

function readCue(node: HTMLElement): Omit<OverflowCueState, "scrollByPage"> {
  const overflows = node.scrollWidth > node.clientWidth + EDGE_PX;
  return {
    overflows,
    canPrev: overflows && node.scrollLeft > EDGE_PX,
    canNext: overflows && node.scrollLeft + node.clientWidth < node.scrollWidth - EDGE_PX,
  };
}

/** Scroll `child` sideways inside `container` without moving the page vertically. */
export function scrollChildIntoStrip(container: HTMLElement, child: HTMLElement): void {
  const start =
    child.getBoundingClientRect().left -
    container.getBoundingClientRect().left +
    container.scrollLeft;
  const width = child.getBoundingClientRect().width;
  const next = Math.max(0, start - (container.clientWidth - width) / 2);
  container.scrollTo({ left: next, behavior: "auto" });
}

/**
 * Keep the selected tab in view after mount, active changes, and whenever the strip or its tabs
 * change size (counts and web fonts load after the first paint, so the strip may only start to
 * overflow later).
 */
export function observeActiveChildInStrip(node: HTMLElement, selector: string): () => void {
  // A timer, not requestAnimationFrame: a hidden tab never runs frames, and reading layout here
  // is synchronous anyway.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const current = node.querySelector<HTMLElement>(selector);
      if (current && node.scrollWidth > node.clientWidth) scrollChildIntoStrip(node, current);
    }, 0);
  };
  run();
  const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(run);
  const observeChildren = () => {
    if (!resize) return;
    resize.observe(node);
    for (const child of Array.from(node.children)) resize.observe(child);
  };
  observeChildren();
  const mutation =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver(() => {
          observeChildren();
          run();
        });
  mutation?.observe(node, { childList: true, subtree: true, characterData: true });
  void document.fonts?.ready.then(run);
  return () => {
    clearTimeout(timer);
    resize?.disconnect();
    mutation?.disconnect();
  };
}

/** Tracks sideways overflow and whether a strip can still scroll toward either edge. */
export function useOverflowCue(ref: RefObject<HTMLElement | null>): OverflowCueState {
  const [cue, setCue] = useState({ overflows: false, canPrev: false, canNext: false });

  const sync = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    setCue(readCue(node));
  }, [ref]);

  const scrollByPage = useCallback(
    (direction: -1 | 1) => {
      const node = ref.current;
      if (!node) return;
      const reduced =
        typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
      node.scrollBy({
        left: node.clientWidth * PAGE_RATIO * direction,
        behavior: reduced ? "auto" : "smooth",
      });
    },
    [ref],
  );

  // The strip node is attached for the life of the host; observers cover later size and children.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `ref.current` is a DOM node, not reactive state.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    sync();
    node.addEventListener("scroll", sync, { passive: true });
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync);
    resize?.observe(node);
    const mutation = typeof MutationObserver === "undefined" ? null : new MutationObserver(sync);
    mutation?.observe(node, { childList: true, subtree: true, characterData: true });
    return () => {
      node.removeEventListener("scroll", sync);
      resize?.disconnect();
      mutation?.disconnect();
    };
  }, [sync]);

  return { ...cue, scrollByPage };
}
